import { DashboardFilters } from "@/components/dashboard/dashboard-filters";
import {
  PROPERTY_ACCESSOR_KEYS,
  StrCatalog,
  validateStrCatalogSearch,
} from "@/components/non_registrant/str-catalog";
import { getJurisdictionsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import { regionQueryParams } from "@/hera-api/regions-api";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useOperatorDashboardState } from "@/hooks/use-operator-dashboard-state";
import { useRegionFilterOptions } from "@/hooks/use-region-filter";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/operator/str_/properties")({
  validateSearch: (search: Record<string, unknown>) =>
    validateStrCatalogSearch(search, "title", PROPERTY_ACCESSOR_KEYS),
  component: PropertiesRoute,
});

function PropertiesRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const dashboardState = useOperatorDashboardState();
  const jurisdictionsQuery = useQuery({
    ...getJurisdictionsOptions({ query: { include_deleted: false } }),
    enabled: isAuthSetupComplete,
  });
  const jurisdictions = jurisdictionsQuery.data?.jurisdictions ?? [];
  const jurisdictionNames = Object.fromEntries(
    jurisdictions.map((jurisdiction) => [jurisdiction.id, jurisdiction.name]),
  );
  const regionScope = jurisdictions.filter(
    (jurisdiction) =>
      dashboardState.selectedJurisdictions.length === 0 ||
      dashboardState.selectedJurisdictions.includes(jurisdiction.id),
  );
  const regionFilter = useRegionFilterOptions({
    jurisdictionIds: regionScope.map((jurisdiction) => jurisdiction.id),
    jurisdictionNames,
    enabled: isAuthSetupComplete,
  });
  const selectedRegionParams = regionQueryParams(
    dashboardState.selectedRegions,
  );
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <StrCatalog
        kind="properties"
        search={search}
        onSearchChange={(next) => {
          void navigate({ search: next, replace: true });
        }}
        propertyScope={{
          jurisdictionIds: dashboardState.selectedJurisdictions,
          regionIds: selectedRegionParams.region_ids ?? [],
          includeUnassignedRegions:
            selectedRegionParams.include_unassigned_regions ?? false,
        }}
        scopeControls={
          <DashboardFilters
            selectedJurisdictions={dashboardState.selectedJurisdictions}
            onSelectedJurisdictionsChange={
              dashboardState.setSelectedJurisdictions
            }
            jurisdictionOptions={jurisdictions.map((jurisdiction) => ({
              value: jurisdiction.id,
              label: jurisdiction.name,
            }))}
            showJurisdictionFilter
            onSelectedRegionsChange={dashboardState.setSelectedRegions}
          />
        }
        sharedFilters={{
          columnFilters: dashboardState.columnFilters,
          setColumnFilters: dashboardState.setColumnFilters,
          selectedJurisdictions: dashboardState.selectedJurisdictions,
          setSelectedJurisdictions: dashboardState.setSelectedJurisdictions,
          jurisdictions: jurisdictions.map((jurisdiction) => ({
            id: jurisdiction.id,
            name: jurisdiction.name,
          })),
          selectedRegions: dashboardState.selectedRegions,
          setSelectedRegions: dashboardState.setSelectedRegions,
          regions: regionFilter.catalog,
        }}
        regionOverlay={{
          selectedJurisdictionIds: dashboardState.selectedJurisdictions,
          portalJurisdictionIds: jurisdictions.map(
            (jurisdiction) => jurisdiction.id,
          ),
          onRegionFilterChange: dashboardState.setSelectedRegions,
        }}
      />
    </div>
  );
}
